import { Exam, MagicWand, Plus, Stack } from "@phosphor-icons/react";
import { lazy, use, useState } from "react";
import { match } from "ts-pattern";

import { Menu, MenuButton, MenuList, MenuTooltip } from "~/components/Menu";
import { Modal } from "~/components/Modal";
import { UserContext } from "~/components/UserContext";
import { useLocale } from "~/hooks/locale";

import { CreateAssignment } from "./CreateAssignment";
import { CreateMenuContext } from "./CreateMenuContext";
import { CreateOption } from "./CreateOption";
import { GeneratePack } from "./GeneratePack";

const AuthForm = lazy(() => import("~/components/AuthForm"));
const CreatePack = lazy(() => import("./CreatePack"));

export type CreateMenuAction = "create" | "generate" | "assignment";

export const CreateMenu: React.FC = () => {
  const { t } = useLocale();
  const user = use(UserContext);
  const [action, setAction] = useState<CreateMenuAction | null>(null);
  return (
    <>
      <Menu>
        <MenuTooltip
          className="max-lg:tooltip"
          placement="left"
          tip={t("create")}
        >
          <MenuButton className="btn btn-secondary btn-sm rounded-full max-lg:px-2 max-md:px-1 md:h-10">
            <Plus className="size-5 max-lg:mx-px" weight="bold" />
            <span className="max-lg:hidden">{t("create")}</span>
          </MenuButton>
        </MenuTooltip>
        <MenuList>
          <CreateMenuContext value={{ action, setAction }}>
            <ul className="menu min-w-72">
              <li>
                <CreateOption
                  icon={Stack}
                  title={t("create_pack")}
                  description={t("create_pack_description")}
                  className="text-pink-500 dark:text-pink-300"
                  action="create"
                />
              </li>
              <li>
                <CreateOption
                  icon={MagicWand}
                  title={t("generate_pack")}
                  description={t("generate_pack_description")}
                  className="text-emerald-500 dark:text-emerald-300"
                  action="generate"
                />
              </li>
              <li>
                <CreateOption
                  icon={Exam}
                  title={t("create_assignment")}
                  description={t("create_assignment_description")}
                  className="text-sky-500 dark:text-sky-300"
                  action="assignment"
                />
              </li>
            </ul>
          </CreateMenuContext>
        </MenuList>
      </Menu>
      <Modal
        type={user && action === "assignment" ? "drawer" : "dialog"}
        isOpen={action !== null}
        onClose={() => {
          setAction(null);
        }}
      >
        {user ? (
          action &&
          match(action)
            .with("create", () => <CreatePack />)
            .with("generate", () => <GeneratePack />)
            .with("assignment", () => <CreateAssignment />)
            .exhaustive()
        ) : (
          <AuthForm />
        )}
      </Modal>
    </>
  );
};
